import { inject, Service } from '@angular/core';
import { MadCloudResult } from '@common/http/models/mad-cloud-result.model';
import { PageDataType } from '@common/paged-data/enums/page-data-type.enum';
import { PageFilterGroupType } from '@common/paged-data/enums/page-filter-group-type.enum';
import { PageFilterOperator } from '@common/paged-data/enums/page-filter-operator.enum';
import { PageFilterType } from '@common/paged-data/enums/page-filter-type.enum';
import { PageFilter } from '@common/paged-data/types/page-filter.type';
import { Page } from '@common/paged-data/types/page.type';
import { ApiService } from '@portal-core/auth/services/api.service';
import { CollectionServiceBase } from '@portal-core/data/collection/services/collection.service.base';
import { GetDataOptions } from '@portal-core/data/common/models/get-data-options.model';
import { LinkService } from '@portal-core/general/services/link.service';
import { EmptySiteKey } from '@portal-core/sites/constants/empty-site-key.constant';
import { SiteGridItem } from '@portal-core/sites/models/site-grid-item.model';
import { SiteUpdate } from '@portal-core/sites/models/site-update.model';
import { Site } from '@portal-core/sites/models/site.model';
import { UserSite } from '@portal-core/sites/models/user-site.model';
import { SitesApiService } from '@portal-core/sites/services/sites-api.service';
import { SitesDataService } from '@portal-core/sites/services/sites-data.service';
import { Team } from '@portal-core/teams/models/team.model';
import { Resettable } from '@portal-core/util/resettable.decorator';
import { Observable, map, switchMap, tap } from 'rxjs';

@Service()
@Resettable()
export class SitesService extends CollectionServiceBase<Site> {
  private readonly sitesApiService = inject(SitesApiService);
  private readonly linkService = inject(LinkService);
  private readonly apiService = inject(ApiService);

  constructor() {
    super(inject(SitesDataService));
  }

  private getLiveSiteUrlTemplate$(): Observable<string> {
    return this.sitesApiService.getLiveSiteUrlTemplate$();
  }

  private getUrlFromTemplate(urlTemplate: string, vanityUrl: string): string {
    return urlTemplate
      .replaceAll('{license}', vanityUrl)
      .replaceAll('{license-subdomain}', vanityUrl + '.');
  }

  getDomainFromUrl(url: string) {
    return url && url.charAt(0) !== '.' && url.charAt(0) !== '/' ?
      new URL(url).hostname : url;
  }

  getLiveSiteUrl$(vanityUrl: string): Observable<string> {
    return this.getLiveSiteUrlTemplate$().pipe(
      map(urlTemplate => this.getUrlFromTemplate(urlTemplate, vanityUrl))
    );
  }

  getLiveSiteDomain$(vanityUrl: string): Observable<string> {
    return this.getLiveSiteUrl$(vanityUrl).pipe(
      map(this.getDomainFromUrl)
    );
  }

  isValidSiteKey(siteKey: string): boolean {
    return siteKey && siteKey !== EmptySiteKey;
  }

  injectSiteStyles$(siteKey: string): Observable<HTMLLinkElement> {
    return this.linkService.inject$('stylesheet', `${this.apiService.centralApiBaseUri}/api/SiteStyles/${siteKey}/css`);
  }

  setSitesAsLive$(siteIds: number[], live: boolean): Observable<void> {
    return this.sitesApiService.setSitesAsLive$(siteIds, live).pipe(
      switchMap((result) => {
        return this.updateItems$(siteIds.map(siteId => {
          return {
            Id: siteId,
            IsLive: live
          };
        })).pipe(map(() => result));
      })
    );
  }

  createSite$(licenseId: number, siteSettings: Partial<SiteUpdate>): Observable<MadCloudResult<Site>> {
    return this.sitesApiService.createSite$(licenseId, siteSettings);
  }

  updateSiteSettings$(siteSettings: Partial<SiteUpdate>): Observable<void> {
    return this.sitesApiService.updateSiteSettings$(siteSettings).pipe(
      tap(() => {
        this.sitesApiService.getSiteGridItem$(siteSettings.Id).subscribe(newSite => {
          this.updateItems$({ [newSite.Id]: newSite });
        });
      })
    );
  }

  addSiteTeams$(siteId: number, teamIds: number[]): Observable<MadCloudResult<void>> {
    return this.sitesApiService.addSiteTeams(siteId, teamIds);
  }

  deleteSiteTeams$(siteId: number, teamIds: number[]): Observable<MadCloudResult<void>> {
    return this.sitesApiService.deleteSiteTeams(siteId, teamIds);
  }

  deleteSites$(siteIds: number[]): Observable<void> {
    return this.sitesApiService.deleteSites$(siteIds).pipe(
      tap(() => {
        this.deleteItems$(siteIds);
      })
    );
  }

  protected override fetchItemById$(itemId: number): Observable<Site> {
    return this.sitesApiService.getSiteById$(itemId);
  }

  protected override fetchItemsById$(itemIds: number[]): Observable<Site[]> {
    return this.sitesApiService.getSitesByIds$(itemIds);
  }

  getSiteGridItem$(siteId: number, options?: GetDataOptions): Observable<SiteGridItem> {
    return this.dataService.getData<SiteGridItem>({
      get: () => this.collectionDataService.getItemById$<SiteGridItem>(siteId),
      fetch: () => this.sitesApiService.getSiteGridItem$(siteId),
      set: newItem => this.collectionDataService.addItems$({ [newItem.Id]: newItem })
    }, options);
  }

  getSitesByLicenseId$(licenseId: number, options: GetDataOptions = null): Observable<Site[]> {
    return this.getDataListItems$('Licenses', licenseId, {
      fetch: () => this.sitesApiService.getSitesByLicenseId$(licenseId).pipe(
        map(sites => {
          if (Array.isArray(sites)) {
            return sites.sort((siteA, siteB) => siteA.Name.localeCompare(siteB.Name));
          }
        })
      )
    }, options);
  }

  getSiteListItemsByLicenseId$(licenseId: number, options: GetDataOptions = null): Observable<Site[]> {
    return this.getDataListItems$('ListItemsByLicense', licenseId, {
      fetch: () => this.sitesApiService.getSiteListItemsByLicenseId$(licenseId).pipe(
        map(sites => {
          if (Array.isArray(sites)) {
            return sites.sort((siteA, siteB) => siteA.Name.localeCompare(siteB.Name));
          }
        })
      )
    }, options);
  }

  getSitesListByLicenseHostMapId$(licenseHostMapId: number, options: GetDataOptions = null): Observable<Site[]> {
    return this.getDataListItems$('LicenseHostMaps', licenseHostMapId, {
      fetch: () => this.sitesApiService.getSitesListByLicenseHostMapId$(licenseHostMapId).pipe(
        map(sites => {
          if (Array.isArray(sites)) {
            return sites.sort((siteA, siteB) => siteA.Name.localeCompare(siteB.Name));
          }
        })
      )
    }, options).pipe(
      // Filter out any sites that are no longer associated with the license host map.
      // This allows the data list to reflect any changes made since last fetching the data list from the server.
      map(sites => {
        return Array.isArray(sites) ? sites.filter(site => site.LicenseHostMapId === licenseHostMapId) : null;
      })
    );
  }

  getSitesPageByLicenseList$(licenseId: number, filter: PageFilter, maxIncludes?: number): Observable<Page<SiteGridItem>> {
    return this.sitesApiService.getSitesPageByLicenseList$(licenseId, filter, maxIncludes);
  }

  getSitesPageByLicense$(licenseId: number, filter: PageFilter): Observable<Page<Site>> {
    return this.sitesApiService.getSitesPageByLicense$(licenseId, filter);
  }

  getSitesListByBuildId$(buildId: number, options: GetDataOptions = null): Observable<Site[]> {
    return this.getDataListItems$('Builds', buildId, {
      fetch: () => this.sitesApiService.getSitesListByBuildId$(buildId).pipe(
        map(sites => {
          if (Array.isArray(sites)) {
            return sites.sort((siteA, siteB) => siteA.Name.localeCompare(siteB.Name));
          }
        })
      )
    }, options).pipe(
      // Filter out any sites that are no longer associated with the build.
      // This allows the data list to reflect any changes made since last fetching the data list from the server.
      map(sites => {
        if (Array.isArray(sites)) {
          return sites.filter(site => {
            if (typeof site.BuildId === 'number') {
              return site.BuildId === buildId;
            } else if (typeof site.LastCompletedBuildId === 'number') {
              return site.LastCompletedBuildId === buildId;
            } else {
              return false;
            }
          });
        }
      })
    );
  }

  getSitesPageByTargetAndBranch$(licenseId: number, projectId: number, targetPath: string, branchName: string, pageNumber: number, perPage: number): Observable<Page<Site>> {
    const filter: PageFilter = {
      Id: 'sites-for-target-and-branch',
      Type: PageFilterGroupType.Custom,
      PageNumber: pageNumber,
      PerPage: perPage,
      OrderBy: 'Name',
      OrderDirection: 'asc',
      Operator: PageFilterOperator.And,
      FilterGroups: [
        {
          Id: 'sites-for-target',
          Type: PageFilterGroupType.Custom,
          Operator: PageFilterOperator.And,
          Filters: [
            { FilterType: PageFilterType.Equals, PropertyName: 'ProjectId', PropertyType: PageDataType.Int, PropertyValue: projectId, },
            { FilterType: PageFilterType.Equals, PropertyName: 'BuildId', PropertyType: PageDataType.Int, PropertyValue: null, },
            { FilterType: PageFilterType.Equals, PropertyName: 'TargetPath', PropertyType: PageDataType.String, PropertyValue: targetPath, }
          ]
        },
        {
          Id: 'sites-for-branch',
          Type: PageFilterGroupType.Custom,
          Operator: PageFilterOperator.Or,
          Filters: [
            { FilterType: PageFilterType.Equals, PropertyName: 'BranchName', PropertyType: PageDataType.String, PropertyValue: null, },
            { FilterType: PageFilterType.Equals, PropertyName: 'BranchName', PropertyType: PageDataType.String, PropertyValue: branchName, }
          ]
        }
      ]
    };

    return this.sitesApiService.getSitesPageByLicense$(licenseId, filter);
  }

  getSitesByTeamIds$(teamIds: number[]): Observable<Site[]> {
    return this.sitesApiService.getSitesByTeamIds$(teamIds);
  }

  getSiteIndexUrl$(siteKey: string): Observable<string> {
    return this.sitesApiService.getSiteIndexUrl$(siteKey);
  }

  getTeamsPageBySiteId$(siteId: number, filter: PageFilter): Observable<Page<Team>> {
    return this.sitesApiService.getTeamsPageBySiteId$(siteId, filter);
  }

  getCurrentUserSites$(licenseId: number, liveSitesOnly: boolean = false): Observable<UserSite[]> {
    return this.sitesApiService.getCurrentUserSites$(licenseId, liveSitesOnly);
  }

  getLicenseId$(siteKey: string): Observable<number> {
    return this.sitesApiService.getLicenseId$(siteKey);
  }

  getVanityValidation$(licenseId: number, vanity: string, hostMapId?: number): Observable<MadCloudResult<void>> {
    return this.sitesApiService.getVanityValidation$(licenseId, vanity, hostMapId);
  }

  getLiveSitesPage$(filter: PageFilter): Observable<Page<SiteGridItem>> {
    return this.sitesApiService.getLiveSitesPage$(filter);
  }

  isLocalOrigin(origin: string): boolean {
    return origin.includes('localhost') || origin.includes('localtest.me');
  }
}
